/* eslint-disable max-classes-per-file */
import { BillOfMaterialsStatus, BomStatus } from '../../lib/types';
import { isSystemError } from '../../lib/errorUtils';

/**
 * Custom error class for BOM timeout errors (retryable)
 */
export class BomTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BomTimeoutError';
  }
}

/**
 * Custom error class for BOM failures (may or may not be retryable)
 */
export class BomFailedError extends Error {
  public readonly isRetryable: boolean;

  constructor(message: string, isRetryable = false) {
    super(message);
    this.name = 'BomFailedError';
    this.isRetryable = isRetryable;
  }
}

/**
 * Simplifies error messages for user-facing display
 * Keeps full detailed messages for console/Sentry
 * @param errorMessage - Full error message
 * @returns Simplified message for users
 */
export function simplifyBomErrorMessage(errorMessage: string): string {
  if (errorMessage.includes('Could not find any entity of type "FittingVariant"')) {
    return 'Could not find fitting';
  }
  return errorMessage;
}

/**
 * Determines if an error is retryable based on error type and message
 * @param error - Error object
 * @returns true if error is retryable (timeout), false otherwise
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof BomTimeoutError) {
    return true;
  }
  if (error instanceof BomFailedError) {
    return error.isRetryable;
  }
  if (error instanceof Error) {
    // System errors are NOT retryable
    // These indicate the backend is in a bad state that won't recover without intervention
    if (isSystemError(error)) {
      return false;
    }

    // Timeout errors are retryable (transient issues)
    const errorMessage = error.message.toLowerCase();
    if (errorMessage.includes('timeout') || errorMessage === 'bom generation timeout') {
      return true;
    }
  }
  return false;
}

/**
 * Gets the batch size for processing BOM requests
 * Can be adjusted via NEXT_PUBLIC_BOM_BATCH_SIZE environment variable
 * @returns Batch size (default: 3)
 */
const getBomBatchSize = (): number => {
  const envValue = process.env.NEXT_PUBLIC_BOM_BATCH_SIZE;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    // Validate: must be a positive integer
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
    console.warn(`Invalid NEXT_PUBLIC_BOM_BATCH_SIZE value: ${envValue}. Using default: 3`);
  }
  return 3; // Default batch size
};

/**
 * Batch size for processing BOM requests in parallel
 * Configurable via NEXT_PUBLIC_BOM_BATCH_SIZE environment variable
 */
export const BOM_BATCH_SIZE = getBomBatchSize();

/**
 * Maximum number of retries for failed BOMs before giving up
 */
export const MAX_BOM_RETRIES = 3;

/**
 * Polling interval in milliseconds for checking BOM status
 * Backend checks every 1 second, so we poll slightly slower to avoid unnecessary requests
 */
const POLL_INTERVAL_MS = 1500;

/**
 * Delay in milliseconds for React state synchronization after state updates
 */
export const STATE_UPDATE_DELAY_MS = 100;

/**
 * Delay in milliseconds between batches of BOM requests
 */
export const BATCH_DELAY_MS = 200;

/**
 * Waits for a BOM to be ready by polling the status
 * @param getBillOfMaterialsStatus - Function to check BOM status
 * @param productId - Product ID
 * @param configuration - Product configuration string
 * @param maxWaitSeconds - Maximum time to wait in seconds (default 90)
 * @returns Promise that resolves with the BOM status when ready or failed
 */
export async function waitForBomReady(
  getBillOfMaterialsStatus: (productId: string, configuration: string) => Promise<BillOfMaterialsStatus>,
  productId: string,
  configuration: string,
  maxWaitSeconds = 90,
): Promise<BillOfMaterialsStatus> {
  const startTime = Date.now();
  const maxWaitMs = maxWaitSeconds * 1000;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const status = await getBillOfMaterialsStatus(productId, configuration);

    if (status.status === 'READY') {
      return status;
    }

    if (status.status === 'FAILED') {
      const errorMessage = status.errorMessage || 'BOM generation failed';
      // Determine if this is a retryable error (timeout-related) or non-retryable (like missing fitting)
      const isRetryable = errorMessage.includes('timeout') || errorMessage === 'BOM generation timeout';
      throw new BomFailedError(errorMessage, isRetryable);
    }

    // Check timeout
    if (Date.now() - startTime > maxWaitMs) {
      throw new BomTimeoutError('BOM generation timeout');
    }

    // Wait before next poll
    // eslint-disable-next-line no-await-in-loop, @typescript-eslint/no-misused-promises, @typescript-eslint/no-floating-promises
    await new Promise<void>((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
  }
}

/**
 * Checks BOM status and requests generation if needed
 * @param requestBillOfMaterials - Function to request BOM generation
 * @param getBillOfMaterialsStatus - Function to check BOM status
 * @param productId - Product ID
 * @param configuration - Product configuration string
 * @returns Promise that resolves with the current BOM status
 */
export async function requestBomIfNeeded(
  requestBillOfMaterials: (productId: string, configuration: string) => Promise<BillOfMaterialsStatus>,
  getBillOfMaterialsStatus: (productId: string, configuration: string) => Promise<BillOfMaterialsStatus>,
  productId: string,
  configuration: string,
): Promise<BillOfMaterialsStatus> {
  // First check current status
  let status = await getBillOfMaterialsStatus(productId, configuration);

  // If no record exists or status is FAILED, request generation
  if (!status.recordCreated || status.status === 'FAILED') {
    status = await requestBillOfMaterials(productId, configuration);
  }

  return status;
}

/**
 * Gets productId and configuration from a product instance
 * Falls back to database lookup if not available (for very old SketchUp definitions)
 * @param productInstance - Product instance from SketchUp
 * @param getProductInstanceFromDb - Optional function to fetch from database if needed
 * @returns Object with productId and configuration, or null if unavailable
 */
export async function getProductInstanceForBom(
  productInstance: { productId?: string; configuration?: string; instanceId?: string },
  getProductInstanceFromDb?: (instanceId: string) => Promise<{ productId: string; configuration: string } | null>,
): Promise<{ productId: string; configuration: string } | null> {
  // If productId and configuration are available, use them
  if (productInstance.productId && productInstance.configuration) {
    return {
      productId: productInstance.productId,
      configuration: productInstance.configuration,
    };
  }

  // Fallback to database lookup if available
  if (getProductInstanceFromDb && productInstance.instanceId) {
    const dbInstance = await getProductInstanceFromDb(productInstance.instanceId);
    if (dbInstance) {
      return dbInstance;
    }
  }

  // If we can't get the required data, return null
  console.warn('Could not get productId and configuration for BOM request', productInstance);
  return null;
}

/**
 * BOM Status Helper Functions
 * These functions handle both new status values (PENDING, READY, FAILED) and legacy boolean values
 */

/**
 * Checks if a BOM status indicates it's pending (not ready yet)
 * @param status - BOM status value (can be BomStatus, boolean, undefined, or null)
 * @returns true if status is PENDING or undefined
 */
export function isBomPending(status: BomStatus | boolean | undefined | null): boolean {
  return status === 'PENDING' || status === undefined;
}

/**
 * Checks if a BOM status indicates it's ready
 * @param status - BOM status value (can be BomStatus, boolean, undefined, or null)
 * @returns true if status is READY or legacy boolean true
 */
export function isBomReady(status: BomStatus | boolean | undefined | null): boolean {
  return status === 'READY' || status === true; // Support legacy boolean
}

/**
 * Checks if a BOM status indicates it failed
 * @param status - BOM status value (can be BomStatus, boolean, undefined, or null)
 * @returns true if status is FAILED
 */
export function isBomFailed(status: BomStatus | boolean | undefined | null): boolean {
  return status === 'FAILED';
}

/**
 * Checks if a BOM status is in a final state (not pending)
 * @param status - BOM status value (can be BomStatus, boolean, undefined, or null)
 * @returns true if status is READY, FAILED, or legacy boolean true
 */
export function isBomInFinalState(status: BomStatus | boolean | undefined | null): boolean {
  return status === 'READY' || status === 'FAILED' || status === true; // Support legacy
}

/**
 * Filters out invalid product instances (those with hash IDs from makeUnique)
 * @param products - Array of product instances
 * @returns Array of valid product instances
 */
export function getValidProducts<T extends { id: string }>(products: T[]): T[] {
  return products.filter((prod) => !prod.id.includes('#'));
}
